import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "antd";
import PageHeader from "Components/PageHeader";
import { DownOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { SbTable, SbNotification } from "uiCore";
import { useMatchThresholds } from "Store/useTalentMobilitySettingsStore";
import { ClickableTableWrapperStyled, STICKY_PAGINATION_HEIGHT } from "../sharedTable.styles";
import { PageTransitionStyled } from "../talentMobility.animations";
import { useTalentMobilityEmployees } from "../useTalentMobilityData";
import { groupTableRowsByRole } from "../talentMobilityEmployees.utils";
import { TEXT } from "../talentMobility.constants";
import {
  PLANNING_TOOLS_MENU_ITEMS,
  PLANNING_TOOLS_ROUTES,
  TOUR_MODULE_KEY,
  CROSS_PAGE_TOUR_PHASE,
  TOUR_COLUMN_CLASS,
  TOUR_FIRST_ROW_CLASS,
  getTourSteps,
} from "./TalentMobilityManagerV2.constants";
import { HeaderActionsRowStyled, PlanningToolsTriggerStyled } from "./TalentMobilityManagerV2.styles";
import useTalentMobilityManagerColumns, { openRoleDetail } from "./useTalentMobilityManagerColumns";
import TalentMobilityRoleEmployeesTable from "./TalentMobilityRoleEmployeesTable";
import TalentMobilityIcon from "Components/SBLayoutIcon/TalentMobilityIcon";

// Guided tour — reuses the shared SbTour helpers
import SbTour from "uiCore/SbTour/SbTour";
import useModuleTour from "uiCore/SbTour/useModuleTour";
import buildTourSteps from "uiCore/SbTour/buildTourSteps";
import spanTourTarget from "uiCore/SbTour/spanTourTarget";
import { TourTargetStyled } from "uiCore/SbTour/SbTour.styles";
import useTourStore from "Store/useTourStore";

import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";


const ANALYTICS_FROM = "talent_mobility_manager";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.TALENT_MOBILITY.MANAGER_DASHBOARD_VIEWED;

const TalentMobilityManagerV2 = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  // This screen has no Mixpanel coverage of its own; the automatic
  // pageview only records the URL, not that the module was reached.
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  const navigate = useNavigate();
  const { employees, updateRoleField, isLoading, error } = useTalentMobilityEmployees();
  const thresholds = useMatchThresholds();
  // everExpandedRowKeys only ever grows -- fed to antd's own
  // expandable.expandedRowKeys so a role's row, once opened, stays mounted
  // and displayed forever after (never flipped back to antd's display:
  // none). That's what leaves room for TalentMobilityRoleEmployeesTable's
  // own CSS transition to animate the close: antd's native toggle would
  // otherwise snap a collapsing row to display: none the instant its key
  // left this list, with no time for any transition to play. openRowKeys is
  // the real open/closed state, toggled freely per click and passed down as
  // isOpen to actually drive that animation.
  const [everExpandedRowKeys, setEverExpandedRowKeys] = useState([]);
  const [openRowKeys, setOpenRowKeys] = useState([]);
  // The wrapper (overflow-y: auto) is what actually scrolls here, not the
  // window — antd's sticky horizontal scrollbar needs it as its container to
  // know when the table's own scrollbar is out of view. Held in state (a
  // callback ref) so the table re-renders with it once it mounts.
  const [scrollContainer, setScrollContainer] = useState(null);
  // Controlled only so the tour's Finish can open the menu for the user.
  const [planningToolsOpen, setPlanningToolsOpen] = useState(false);

  useEffect(() => {
    if (!error) return;
    SbNotification.error(
      "Unable to load employees",
      error?.response?.data?.message || "Please refresh the page to try again."
    );
  }, [error]);

  // One row per role (jd_sb_unique_id) — recomputed whenever an employee's
  // Open/Discoverable checkbox changes (updateRoleField updates `employees`),
  // so a role's counts and its expanded sub-table stay in sync automatically.
  const roles = useMemo(() => groupTableRowsByRole(employees), [employees]);
  const columns = useTalentMobilityManagerColumns({ roles, thresholds, navigate, openRowKeys });

  // ── Guided tour ────────────────────────────────────────────────────────────
  // Shared tour wiring: controller + seen-flag + auto-launch + global-button
  // registration. Auto-launch is suppressed while a cross-page hop is in flight.
  const crossPageTourPhase = useTourStore((s) => s.crossPageTourPhase);
  const setCrossPageTourPhase = useTourStore((s) => s.setCrossPageTourPhase);
  const clearCrossPageTourPhase = useTourStore((s) => s.clearCrossPageTourPhase);
  const {
    open: tourOpen,
    current: tourStep,
    remountKey: tourRunId,
    begin,
    goToStep: goToTourStep,
    close: closeMainTour,
    markSeen,
  } = useModuleTour({ moduleKey: TOUR_MODULE_KEY, autoLaunch: !crossPageTourPhase });

  const tourTargetRefs = {
    rolesTable: useRef(null),
    emptyState: useRef(null),
    planningTools: useRef(null),
  };
  // The table wrapper is both the sticky scrollbar's container and the tour's
  // roles-table / empty-state target (only one of those steps runs per tour).
  const rolesTableRef = tourTargetRefs.rolesTable;
  const emptyStateRef = tourTargetRefs.emptyState;
  const setTableWrapper = useCallback(
    (node) => {
      setScrollContainer(node);
      rolesTableRef.current = node;
      emptyStateRef.current = node;
    },
    [rolesTableRef, emptyStateRef]
  );

  // Still-loading counts as has-data (same as Jobs Library), so an early
  // auto-launch doesn't lock into the empty branch before the roster arrives.
  const isRosterEmpty = !isLoading && roles.length === 0;
  const branchTourSteps = getTourSteps(isRosterEmpty);
  // Steps another page shows (Role Detail, Candidate Profile, the Planning
  // Tools pages) aren't run here — advancing onto one hops to that page.
  const tourSteps = branchTourSteps.filter((step) => !step.page);

  const closeTour = () => {
    markSeen();
    closeMainTour();
  };

  // Finish greeting primary → open the Planning Tools menu.
  const finishTour = () => {
    markSeen();
    closeMainTour();
    setPlanningToolsOpen(true);
  };

  // How the tour reaches each page it hops to from here. Role Detail opens the
  // first visible role, exactly as clicking its Title would.
  const hopToTourPage = (phase) => {
    if (phase === CROSS_PAGE_TOUR_PHASE.ROLE_DETAIL) {
      if (!roles.length) return false;
      openRoleDetail(navigate, roles[0]);
      return true;
    }
    if (phase === CROSS_PAGE_TOUR_PHASE.REDEPLOYMENT) {
      navigate(PLANNING_TOOLS_ROUTES.redeployment);
      return true;
    }
    return false;
  };

  const changeTourStep = (step) => {
    const nextSpecStep = branchTourSteps[branchTourSteps.indexOf(tourSteps[tourStep]) + 1];
    if (step > tourStep && nextSpecStep?.page) {
      setCrossPageTourPhase(nextSpecStep.page);
      if (hopToTourPage(nextSpecStep.page)) return;
      clearCrossPageTourPhase();
    }
    goToTourStep(step);
  };

  // Returning from another page's steps → resume at the step it hands back to
  // (Planning Tools after the Candidate Profile, Finish after View Plans).
  useEffect(() => {
    if (isLoading) return;
    const resumeAt = tourSteps.findIndex((step) => step.resumePhase && step.resumePhase === crossPageTourPhase);
    if (resumeAt < 0) return;
    clearCrossPageTourPhase();
    begin(resumeAt);
  }, [crossPageTourPhase, isLoading, tourSteps, clearCrossPageTourPhase, begin]);

  // Column / row steps target the real table cells by class (AntD duplicates
  // the header title node); the rest use their element ref. Greeting
  // (Welcome / Finish) steps get their icon/actions.
  const tourSelectors = {
    priority: `.${TOUR_COLUMN_CLASS.priority}`,
    matches: `.${TOUR_COLUMN_CLASS.matches}`,
    roleRow: `.${TOUR_FIRST_ROW_CLASS} .${TOUR_COLUMN_CLASS.expand}`,
    roleTitle: `.${TOUR_FIRST_ROW_CLASS} .${TOUR_COLUMN_CLASS.roleTitle}`,
  };
  // Steps whose target isn't a single element: the Roles Table step lights up
  // the (sticky) header row rather than the whole, taller-than-the-viewport
  // table; Open & Discoverable spans both header cells.
  const tourTargetResolvers = {
    rolesTable: () => rolesTableRef.current?.querySelector(".ant-table-header") ?? rolesTableRef.current,
    openDiscoverable: () =>
      spanTourTarget(
        document.querySelector(`.${TOUR_COLUMN_CLASS.openDiscoverable}`),
        document.querySelector(`.${TOUR_COLUMN_CLASS.discoverable}`)
      ),
  };
  const { steps: builtTourSteps, actionsRender: renderTourActions, indicatorsRender: renderTourIndicators } =
    buildTourSteps(tourSteps, {
      resolveTarget: (step) => {
        if (tourTargetResolvers[step.key]) return tourTargetResolvers[step.key]();
        return tourSelectors[step.key]
          ? document.querySelector(tourSelectors[step.key])
          : tourTargetRefs[step.key]?.current;
      },
      greeting: {
        icon: (step, isLast) => (isLast ? <CheckCircleOutlined /> : <TalentMobilityIcon />),
        onPrimary: (step, index, isLast) => (isLast ? finishTour() : changeTourStep(index + 1)),
        onSecondary: () => closeTour(),
      },
    });

  const toggleExpandedRow = useCallback((rowKey) => {
    setOpenRowKeys((previous) =>
      previous.includes(rowKey) ? previous.filter((key) => key !== rowKey) : [...previous, rowKey]
    );
    setEverExpandedRowKeys((previous) => (previous.includes(rowKey) ? previous : [...previous, rowKey]));
  }, []);

  return (
    <PageTransitionStyled>
      <PageHeader
        title={TEXT.PAGE_TITLE}
        subtitle={TEXT.PAGE_DESCRIPTION}
        actions={
          <HeaderActionsRowStyled>
            <TourTargetStyled ref={tourTargetRefs.planningTools}>
              <Dropdown
                trigger={["click"]}
                open={planningToolsOpen}
                onOpenChange={setPlanningToolsOpen}
                menu={{
                  items: PLANNING_TOOLS_MENU_ITEMS,
                  onClick: ({ key }) => {
                    setPlanningToolsOpen(false);
                    navigate(PLANNING_TOOLS_ROUTES[key]);
                  },
                }}
              >
                <PlanningToolsTriggerStyled>
                  {TEXT.BTN_PLANNING_TOOLS} <DownOutlined />
                </PlanningToolsTriggerStyled>
              </Dropdown>
            </TourTargetStyled>
          </HeaderActionsRowStyled>
        }
      />

      {/* No purpose-built empty state yet — the tour's empty-branch step
          spotlights the table itself, showing AntD's own "No data". */}
      <ClickableTableWrapperStyled ref={setTableWrapper} $stickyScrollbar>
        <SbTable
          columns={columns}
          dataSource={roles}
          loading={isLoading}
          rowKey="id"
          // offsetScroll parks the floating horizontal scrollbar just above the
          // sticky pagination bar (STICKY_PAGINATION_HEIGHT), so sideways
          // scrolling is reachable without first scrolling to the last row.
          sticky={{
            offsetScroll: STICKY_PAGINATION_HEIGHT,
            getContainer: () => scrollContainer || window,
          }}
          // Eleven columns overflow anything narrower than a large desktop.
          // max-content keeps every column at its natural width (no squeezing
          // headers into two lines) and, more to the point, is what lets the
          // last two columns pin themselves right (fixed: "right" in
          // useTalentMobilityManagerColumns) instead of scrolling off the
          // edge with the sort control on them.
          scroll={{ x: "max-content" }}
          rowClassName={(record, index) => (index === 0 ? TOUR_FIRST_ROW_CLASS : "")}
          onRow={(record) => ({ onClick: () => toggleExpandedRow(record.id) })}
          expandable={{
            expandedRowKeys: everExpandedRowKeys,
            showExpandColumn: false,
            rowExpandable: () => true,
            // Unconditional, not just while closed -- antd's own cell
            // padding around expandedRowRender's content would otherwise
            // still show as a fixed-height empty gap even at
            // grid-template-rows: 0fr (see sharedTable.styles.js), since
            // that padding lives one level above what the animation
            // collapses. NestedTableWrapperStyled already supplies the
            // panel's real padding, so this is pure redundant space either way.
            expandedRowClassName: () => "tm-role-panel-cell",
            expandedRowRender: (record) => (
              <TalentMobilityRoleEmployeesTable
                role={record}
                updateRoleField={updateRoleField}
                isOpen={openRowKeys.includes(record.id)}
              />
            ),
          }}
        />
      </ClickableTableWrapperStyled>

      {/* ── Guided tour ── */}
      <SbTour
        key={tourRunId}
        open={tourOpen}
        current={tourStep}
        onChange={changeTourStep}
        steps={builtTourSteps}
        onClose={closeTour}
        actionsRender={renderTourActions}
        indicatorsRender={renderTourIndicators}
        // Spotlight flush with the target. AntD's default 6px offset pads every
        // side, so a table cell's highlight spilled over the row above and into
        // the first body row. Tour-level only — AntD has no per-step gap.
        gap={{ offset: 0, radius: 2 }}
      />
    </PageTransitionStyled>
  );
};

export default TalentMobilityManagerV2;
